import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../lib/cn';

/**
 * Buttons, from section 03 of the kit.
 *
 * State layers are hover 6% and press 10% ink, at 180ms on the emphasised
 * easing. Every variant is a pill; nothing in this system has a square corner.
 */

type Variant = 'ink' | 'secondary' | 'text' | 'destructive' | 'spark' | 'go';
type Size = 'md' | 'sm';

const base =
  'inline-flex items-center justify-center gap-[7px] rounded-pill whitespace-nowrap ' +
  'transition-colors duration-base ease-emphasis disabled:cursor-not-allowed';

const variants: Record<Variant, string> = {
  // Buttons are buttons: a filled primary in the light, green to start,
  // red to stop, a hairline for the secondary, text for the quiet ones.
  ink: 'bg-ink text-on-ink hover:bg-ink-soft active:bg-ink-press disabled:bg-canvas-tint disabled:text-faint',
  spark: 'bg-ink text-on-ink hover:bg-ink-soft active:bg-ink-press disabled:bg-canvas-tint disabled:text-faint',
  go: 'bg-go text-on-go hover:brightness-110 active:brightness-95 disabled:bg-canvas-tint disabled:text-faint',
  secondary:
    'border border-line-strong text-ink hover:border-ink hover:bg-[var(--state-hover)] disabled:border-line disabled:text-faint',
  text: 'text-muted hover:text-ink hover:underline disabled:text-faint disabled:no-underline',
  destructive:
    'bg-stop text-on-stop hover:brightness-110 active:brightness-95 disabled:bg-canvas-tint disabled:text-faint',
};

const sizes: Record<Size, string> = {
  md: 'h-10 px-[18px] text-body',
  sm: 'h-[34px] px-[14px] text-chip',
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  /** Rendered before the label. Tightens the left padding, as in the mock. */
  icon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'ink', size = 'md', icon, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        base,
        variants[variant],
        sizes[size],
        icon && size === 'md' && 'pl-[15px]',
        className,
      )}
      {...props}
    >
      {icon}
      {children}
    </button>
  );
});

type IconVariant = 'raised' | 'ink' | 'running' | 'ghost';

const iconVariants: Record<IconVariant, string> = {
  /** The default: a floating white pill on the canvas. */
  raised: 'h-10 w-10 bg-paper border border-line-strong hover:border-ink',
  ink: 'h-10 w-10 bg-ink text-on-ink hover:bg-ink-soft',
  running: 'h-10 w-10 bg-running-tint text-running hover:brightness-95',
  /** Inline, inside a row or a card. No elevation of its own. */
  ghost: 'h-[34px] w-[34px] text-muted hover:bg-[var(--state-hover)] hover:text-ink',
};

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: IconVariant;
  /** Required: an icon alone tells a screen reader nothing. */
  label: string;
  /** Small ink dot in the corner, for unread notifications. */
  badge?: boolean;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { variant = 'raised', label, badge, className, children, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center rounded-pill',
        'transition-colors duration-base ease-emphasis disabled:cursor-not-allowed disabled:opacity-50',
        iconVariants[variant],
        className,
      )}
      {...props}
    >
      {children}
      {badge && (
        <span className="absolute right-[10px] top-[9px] h-[7px] w-[7px] rounded-pill border-[1.5px] border-paper bg-ink" />
      )}
    </button>
  );
});
